import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { sendNotification } from '@/lib/notifications';
import { AppShell3 } from '@/components/app-shell-3';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  CheckCircle2,
  Edit2,
  Eye,
  Trash2,
  Plus,
  X,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageTransition } from '@/components/PageTransition';

export function GenerateQuizPage() {
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Generate Quiz');

  const [loading, setLoading] = useState(true);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditingQuiz, setIsEditingQuiz] = useState(false);
  const [questionTypeCache, setQuestionTypeCache] = useState({});
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  const [savedData, setSavedData] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [editedQuizData, setEditedQuizData] = useState(null);

  // Validate quiz before saving
  const validateQuiz = (quiz) => {
    if (!quiz) return { valid: false, errors: ['Quiz data is missing'] };

    const errors = [];

    // Check if quiz has a title
    if (!quiz.quizTitle || !quiz.quizTitle.trim()) {
      errors.push('Quiz title is required');
    }

    // Check if there are questions
    if (!quiz.questions || quiz.questions.length === 0) {
      errors.push('Quiz must have at least one question');
    } else {
      // Validate each question
      quiz.questions.forEach((question, index) => {
        const questionNum = index + 1;

        // Check question text
        if (!question.question || !question.question.trim()) {
          errors.push(`Question ${questionNum}: Question text is required`);
        }

        // Check question type
        if (!question.type) {
          errors.push(`Question ${questionNum}: Question type is required`);
        }

        // Check correct answer based on type
        if (question.type === 'multiple_choice') {
          if (!question.options || question.options.length < 2) {
            errors.push(`Question ${questionNum}: Must have at least 2 options`);
          }
          if (!question.correct_answer || question.correct_answer.trim() === '') {
            errors.push(`Question ${questionNum}: Correct answer is required`);
          }
        } else if (question.type === 'true_false') {
          if (question.correct_answer === undefined || question.correct_answer === null) {
            errors.push(`Question ${questionNum}: Correct answer (True/False) is required`);
          }
        } else if (question.type === 'short_answer') {
          if (!question.correct_answer || question.correct_answer.trim() === '') {
            errors.push(`Question ${questionNum}: Correct answer is required`);
          }
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Load saved data from sessionStorage
  useEffect(() => {
    const storedData = sessionStorage.getItem('quizGenerationData');
    if (!storedData) {
      toast.error('No data found. Redirecting...');
      navigate(-1);
      return;
    }

    try {
      const data = JSON.parse(storedData);
      setSavedData(data);
      setLoading(false);

      // Auto-start quiz generation based on post type
      if (data.postType === 'document' && data.formData.documentUrl) {
        generateQuiz(null, data.formData.title, data.formData.documentUrl, 'document');
      } else if (data.postType === 'text' && data.formData.content) {
        generateQuiz(data.formData.content, data.formData.title, null, 'text');
      } else if (data.formData.transcriptionRaw) {
        generateQuiz(data.formData.transcriptionRaw, data.formData.title, data.formData.videoUrl, 'video');
      }
    } catch (error) {
      console.error('Failed to parse saved data:', error);
      toast.error('Invalid data. Redirecting...');
      navigate(-1);
    }
  }, [navigate]);

  const generateQuiz = async (textContent, title, mediaUrl, sourceType = 'video') => {
    setGeneratingQuiz(true);

    try {
      let response;

      if (sourceType === 'text') {
        // For text posts, call the N8N text-quiz endpoint directly
        const requestBody = {
          postTitle: title,
          sourceType: 'text',
          textContent: textContent,
        };

        response = await fetch('https://n8n.add.ai/webhook/text-quiz', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
      } else {
        // For video and document posts, use the Supabase proxy
        const requestBody = {
          postTitle: title,
          sourceType: sourceType,
        };

        if (sourceType === 'document') {
          // For documents, pass the document URL (N8N will extract text and generate quiz)
          requestBody.documentUrl = mediaUrl;
        } else {
          // For videos, pass the video URL (proxy will transcribe)
          requestBody.videoUrl = mediaUrl;
        }

        // Get the user's session token for authorization
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Not authenticated. Please log in and try again.');
        }

        response = await fetch('https://yqmlqctpakqxtwzrdofl.supabase.co/functions/v1/proxy-quiz-generator', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to generate quiz: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Quiz generator raw response:', responseText);

      // Handle empty responses
      if (!responseText || responseText.trim() === '' || responseText === '{}' || responseText === '{"text":""}') {
        throw new Error('No quiz data received from server. Please try again.');
      }

      const data = JSON.parse(responseText);
      console.log('Quiz generator parsed response:', data);

      // Check for empty text response (N8N returning nothing)
      if (data.text === '' || (Object.keys(data).length === 1 && data.text !== undefined)) {
        throw new Error('The quiz generator returned an empty response. Please ensure the document is accessible and try again.');
      }

      let quizContent = null;

      // Parse quiz from response - flexible handling for different formats
      if (data.quiz) {
        // Format: { quiz: "..." } or { quiz: {...} }
        if (typeof data.quiz === 'string') {
          quizContent = JSON.parse(data.quiz);
        } else {
          quizContent = data.quiz;
        }
      } else if (data.quizTitle || data.questions) {
        // Format: { quizTitle: "...", questions: [...] }
        quizContent = data;
      } else if (data.title || data.Title) {
        // Format: { title: "...", questions: [...] }
        quizContent = {
          quizTitle: data.title || data.Title,
          quizDescription: data.description || data.Description || '',
          questions: data.questions || data.Questions || []
        };
      } else if (Array.isArray(data) && data[0]?.message?.content) {
        // Format: OpenAI chat completion array [{ message: { content: "..." } }]
        const content = data[0].message.content;
        quizContent = typeof content === 'string' ? JSON.parse(content) : content;
      } else if (data.message?.content) {
        // Format: OpenAI single message { message: { content: "..." } }
        const content = data.message.content;
        quizContent = typeof content === 'string' ? JSON.parse(content) : content;
      } else if (Array.isArray(data) && data.length > 0 && data[0].question) {
        // Format: direct array of questions
        quizContent = {
          quizTitle: savedData?.formData?.title || 'Quiz',
          quizDescription: '',
          questions: data
        };
      }

      console.log('Parsed quizContent:', quizContent);

      if (quizContent && (quizContent.questions || quizContent.Questions)) {
        const questions = quizContent.questions || quizContent.Questions;
        // Normalize field names
        const validQuestions = questions
          .map(q => ({
            ...q,
            // Use ?? for correct_answer to handle boolean false values properly
            correct_answer: q.correctAnswer ?? q.correct_answer ?? q.answer ?? q.Answer,
            type: q.type || q.Type || 'multiple_choice',
            question: q.question || q.Question || q.text || q.Text,
            options: q.options || q.Options || q.choices || q.Choices,
            explanation: q.explanation || q.Explanation
          }))
          .filter(q => {
            // Be more lenient - just need question text
            return q.question && q.question.trim() !== '';
          });

        const finalQuiz = {
          quizTitle: quizContent.quizTitle || quizContent.title || quizContent.Title || savedData?.formData?.title || 'Quiz',
          quizDescription: quizContent.quizDescription || quizContent.description || quizContent.Description || '',
          questions: validQuestions
        };

        console.log('Final quiz data:', finalQuiz);
        setQuizData(finalQuiz);
        toast.success('Quiz generated successfully!');
      } else {
        console.error('Could not parse quiz data:', data);
        throw new Error('Invalid quiz data received - no questions found');
      }
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast.error('Failed to generate quiz. Please try again.');
    } finally {
      setGeneratingQuiz(false);
    }
  };

  const handleCreatePost = async () => {
    if (!quizData || !quizData.questions || quizData.questions.length === 0) {
      toast.error('Please generate a quiz first');
      return;
    }

    // Validate quiz before creating post
    const validation = validateQuiz(quizData);
    if (!validation.valid) {
      // Show all validation errors
      validation.errors.forEach(error => {
        toast.error(error);
      });
      return;
    }

    setSubmitting(true);

    try {
      const { formData, postType, visibilityType, projectId, stackId } = savedData;

      // Save metadata to GCS if needed
      let metadataUrl = null;
      if (formData.n8nFullResponse && formData.videoUrl) {
        // (Metadata saving logic - same as CreatePostPage)
      }

      // Prepare post data
      const postData = {
        title: formData.title,
        content: formData.content || '', // content cannot be null
        link_url: formData.linkUrl || null,
        image_url: formData.imageUrl || null,
        image_caption: formData.imageCaption || null,
        video_url: formData.videoUrl || null,
        audio_url: formData.audioUrl || null,
        post_type: postType,
        company_id: company.id,
        created_by: userProfile.id,
        project_id: visibilityType === 'space' ? (projectId || formData.selectedProjectIds?.[0]) : null,
        selected_project_ids: visibilityType === 'space' ? (formData.selectedProjectIds || (projectId ? [projectId] : [])) : [],
        requires_reading: formData.requiresReading || false,
        requires_understanding: formData.requiresUnderstanding || false,
        has_quiz: true,
        quiz_data: quizData,
        transcription_raw: formData.transcriptionRaw || null,
        transcription_timestamps: formData.transcriptionTimestamps || null,
        metadata_url: metadataUrl,
        // Document fields
        document_url: postType === 'document' ? formData.documentUrl : null,
        document_name: postType === 'document' ? formData.documentName : null,
        document_type: postType === 'document' ? formData.documentType : null,
        document_size: postType === 'document' ? formData.documentSize : null,
        document_text: postType === 'document' ? formData.documentText : null,
      };

      // Insert post
      const { data: post, error: postError } = await supabase
        .from('message_board_posts')
        .insert([postData])
        .select()
        .single();

      if (postError) throw postError;

      // Handle multi-space posts
      if (visibilityType === 'space' && formData.selectedProjectIds && formData.selectedProjectIds.length > 1) {
        const additionalPosts = formData.selectedProjectIds.slice(1).map(projId => ({
          ...postData,
          project_id: projId
        }));

        const { error: bulkError } = await supabase
          .from('message_board_posts')
          .insert(additionalPosts);

        if (bulkError) throw bulkError;
      }

      // Send notifications
      await sendNotification({
        type: 'new_message_board_post',
        recipientIds: 'all_company_members',
        companyId: company.id,
        projectId: projectId || formData.selectedProjectIds?.[0],
        data: {
          postId: post.id,
          postTitle: formData.title,
          creatorName: `${userProfile.first_name} ${userProfile.last_name}`,
          postType: postType
        }
      });

      toast.success('Post created successfully!');
      sessionStorage.removeItem('quizGenerationData');

      // Navigate back
      if (projectId) {
        navigate(`/space/${projectId}/message-board`);
      } else if (stackId) {
        navigate(`/stack/${stackId}/message-board`);
      } else {
        navigate('/posts');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppShell3>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <div className='min-h-screen'>
          <div className='max-w-5xl mx-auto p-6'>
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-3'>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => {
                        if (window.confirm('Are you sure you want to go back? Quiz progress will be lost.')) {
                          sessionStorage.removeItem('quizGenerationData');
                          navigate(-1);
                        }
                      }}
                    >
                      <ArrowLeft className='h-5 w-5' />
                    </Button>
                    <div>
                      <h1 className='text-2xl font-bold'>Generate Quiz</h1>
                      <p className='text-sm text-muted-foreground mt-1'>
                        AI is creating quiz questions from your {savedData?.postType === 'text' ? 'content' : savedData?.postType || 'content'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className='space-y-6'>
                {/* Generating Quiz State */}
                {generatingQuiz && (
                  <div className='p-8 border rounded-lg bg-blue-50 text-center space-y-4'>
                    <div className='flex items-center justify-center gap-3'>
                      <LoadingSpinner />
                      <Sparkles className='h-6 w-6 text-blue-600 animate-pulse' />
                    </div>
                    <div>
                      <h3 className='font-semibold text-blue-900'>Generating Quiz Questions...</h3>
                      <p className='text-sm text-blue-700 mt-1'>
                        AI is analyzing your {savedData?.postType === 'text' ? 'content' : savedData?.postType || 'content'} and creating quiz questions. This may take a minute.
                      </p>
                    </div>
                  </div>
                )}

                {/* Quiz Display/Edit - Using the same component structure as CreatePostPage */}
                {quizData && !generatingQuiz && (
                  <div className='space-y-4 p-4 border rounded-lg bg-green-50'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2'>
                        <CheckCircle2 className='h-5 w-5 text-green-600' />
                        <label className='text-sm font-medium text-green-900'>
                          {isEditingQuiz ? 'Editing Quiz' : 'Quiz Generated Successfully'}
                        </label>
                      </div>
                      <div className='flex items-center gap-2'>
                        {isEditingQuiz ? (
                          <>
                            <Button
                              type='button'
                              variant='outline'
                              size='sm'
                              onClick={() => {
                                setIsEditingQuiz(false);
                                setEditedQuizData(null);
                                setQuestionTypeCache({});
                              }}
                              className='bg-white'
                            >
                              <X className='h-4 w-4 mr-1' />
                              Cancel
                            </Button>
                            <Button
                              type='button'
                              size='sm'
                              onClick={() => {
                                // Validate quiz before saving
                                const validation = validateQuiz(editedQuizData);

                                if (!validation.valid) {
                                  // Show all validation errors
                                  validation.errors.forEach(error => {
                                    toast.error(error);
                                  });
                                  return;
                                }

                                // If valid, save changes
                                setQuizData(editedQuizData);
                                setIsEditingQuiz(false);
                                setEditedQuizData(null);
                                setQuestionTypeCache({});
                                toast.success('Quiz changes saved');
                              }}
                              className='bg-green-600 hover:bg-green-700 text-white'
                            >
                              <CheckCircle2 className='h-4 w-4 mr-1' />
                              Save Changes
                            </Button>
                          </>
                        ) : (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => {
                              setEditedQuizData(JSON.parse(JSON.stringify(quizData)));
                              setIsEditingQuiz(true);
                            }}
                            className='bg-white'
                          >
                            <Edit2 className='h-4 w-4 mr-1' />
                            Edit Quiz
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className='space-y-4 bg-white p-4 rounded-md'>
                      {/* Quiz Title & Description */}
                      {isEditingQuiz ? (
                        <div className='space-y-3 pb-4 border-b'>
                          <div>
                            <label className='text-sm font-medium mb-1 block'>Quiz Title</label>
                            <Input
                              value={editedQuizData.quizTitle}
                              onChange={(e) => setEditedQuizData({ ...editedQuizData, quizTitle: e.target.value })}
                              placeholder='Enter quiz title'
                            />
                          </div>
                          <div>
                            <label className='text-sm font-medium mb-1 block'>Quiz Description</label>
                            <Textarea
                              value={editedQuizData.quizDescription}
                              onChange={(e) => setEditedQuizData({ ...editedQuizData, quizDescription: e.target.value })}
                              placeholder='Enter quiz description'
                              rows={2}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className='pb-4 border-b'>
                          <h4 className='font-semibold text-gray-900'>{quizData.quizTitle}</h4>
                          <p className='text-sm text-gray-600 mt-1'>{quizData.quizDescription}</p>
                        </div>
                      )}

                      {/* Questions List */}
                      <div className='space-y-4'>
                        <p className='text-sm font-medium text-gray-700'>
                          {(isEditingQuiz ? editedQuizData : quizData).questions?.length || 0} Questions
                        </p>

                        {(isEditingQuiz ? editedQuizData : quizData).questions?.map((question, qIndex) => (
                          <div key={qIndex} className='p-4 border rounded-lg bg-gray-50 space-y-3'>
                            {/* Question Header */}
                            <div className='flex items-start justify-between gap-2'>
                              <div className='flex-1'>
                                {isEditingQuiz ? (
                                  <div className='space-y-3'>
                                    <div className='flex items-center justify-between'>
                                      <label className='text-xs font-medium text-gray-500'>
                                        Question {qIndex + 1}
                                      </label>
                                      <Select
                                        value={question.type}
                                        onValueChange={(value) => {
                                          const newQuestions = [...editedQuizData.questions];
                                          const oldType = newQuestions[qIndex].type;
                                          const cacheKey = `q${qIndex}`;

                                          // Cache the current state before changing type
                                          const newCache = { ...questionTypeCache };
                                          if (!newCache[cacheKey]) {
                                            newCache[cacheKey] = {};
                                          }

                                          // Save current type's data to cache
                                          newCache[cacheKey][oldType] = {
                                            options: newQuestions[qIndex].options ? [...newQuestions[qIndex].options] : undefined,
                                            correct_answer: newQuestions[qIndex].correct_answer
                                          };

                                          // Update question type
                                          newQuestions[qIndex].type = value;

                                          // Try to restore from cache first, otherwise create defaults
                                          if (newCache[cacheKey][value]) {
                                            // Restore cached data for this type
                                            newQuestions[qIndex].options = newCache[cacheKey][value].options;
                                            newQuestions[qIndex].correct_answer = newCache[cacheKey][value].correct_answer;
                                          } else {
                                            // No cache, create defaults
                                            if (value === 'multiple_choice') {
                                              newQuestions[qIndex].options = ['Option 1', 'Option 2', 'Option 3', 'Option 4'];
                                              newQuestions[qIndex].correct_answer = 'Option 1';
                                            } else if (value === 'true_false') {
                                              newQuestions[qIndex].options = undefined;
                                              newQuestions[qIndex].correct_answer = true;
                                            } else if (value === 'short_answer') {
                                              newQuestions[qIndex].options = undefined;
                                              newQuestions[qIndex].correct_answer = '';
                                            }
                                          }

                                          setQuestionTypeCache(newCache);
                                          setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                        }}
                                      >
                                        <SelectTrigger className='w-[180px] h-8 bg-white'>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value='multiple_choice'>Multiple Choice</SelectItem>
                                          <SelectItem value='true_false'>True/False</SelectItem>
                                          <SelectItem value='short_answer'>Text Answer</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <Textarea
                                      value={question.question}
                                      onChange={(e) => {
                                        const newQuestions = [...editedQuizData.questions];
                                        newQuestions[qIndex].question = e.target.value;
                                        setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                      }}
                                      placeholder='Enter question text'
                                      rows={2}
                                      className='bg-white'
                                    />
                                  </div>
                                ) : (
                                  <div>
                                    <div className='flex items-center gap-2 mb-1'>
                                      <span className='text-xs font-medium text-gray-500'>
                                        Question {qIndex + 1}
                                      </span>
                                      <Badge variant='outline' className='text-xs'>
                                        {question.type === 'multiple_choice' ? 'Multiple Choice' :
                                         question.type === 'true_false' ? 'True/False' :
                                         'Text Answer'}
                                      </Badge>
                                    </div>
                                    <p className='text-sm font-medium text-gray-900 mt-1'>
                                      {question.question}
                                    </p>
                                  </div>
                                )}
                              </div>
                              {isEditingQuiz && (
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => {
                                    const newQuestions = editedQuizData.questions.filter((_, i) => i !== qIndex);
                                    setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                  }}
                                  className='text-red-600 hover:text-red-700 hover:bg-red-50'
                                >
                                  <Trash2 className='h-4 w-4' />
                                </Button>
                              )}
                            </div>

                            {/* Answer Options */}
                            <div className='space-y-2'>
                              {/* Multiple Choice Questions */}
                              {question.options && question.options.length > 0 ? (
                                <>
                                  <label className='text-xs font-medium text-gray-500'>Options</label>
                                  {question.options.map((option, oIndex) => (
                                    <div key={oIndex} className='flex items-center gap-2'>
                                      <div className={`flex-1 p-2 rounded border ${
                                        question.correct_answer === option
                                          ? 'bg-green-50 border-green-300'
                                          : 'bg-white border-gray-200'
                                      }`}>
                                        {isEditingQuiz ? (
                                          <div className='flex items-center gap-2'>
                                            <Input
                                              value={option}
                                              onChange={(e) => {
                                                const newQuestions = [...editedQuizData.questions];
                                                newQuestions[qIndex].options[oIndex] = e.target.value;
                                                setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                              }}
                                              placeholder={`Option ${oIndex + 1}`}
                                              className='flex-1'
                                            />
                                            <Button
                                              type='button'
                                              variant='ghost'
                                              size='sm'
                                              onClick={() => {
                                                const newQuestions = [...editedQuizData.questions];
                                                newQuestions[qIndex].correct_answer = option;
                                                setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                              }}
                                              className={`${
                                                question.correct_answer === option
                                                  ? 'text-green-600'
                                                  : 'text-gray-400 hover:text-green-600'
                                              }`}
                                              title='Mark as correct answer'
                                            >
                                              <CheckCircle2 className='h-4 w-4' />
                                            </Button>
                                          </div>
                                        ) : (
                                          <div className='flex items-center gap-2 text-sm'>
                                            {question.correct_answer === option && (
                                              <CheckCircle2 className='h-4 w-4 text-green-600 flex-shrink-0' />
                                            )}
                                            <span className={question.correct_answer === option ? 'font-medium' : ''}>
                                              {option}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      {isEditingQuiz && question.options.length > 2 && (
                                        <Button
                                          type='button'
                                          variant='ghost'
                                          size='sm'
                                          onClick={() => {
                                            const newQuestions = [...editedQuizData.questions];
                                            newQuestions[qIndex].options = newQuestions[qIndex].options.filter((_, i) => i !== oIndex);
                                            // If we deleted the correct answer, reset it
                                            if (newQuestions[qIndex].correct_answer === option) {
                                              newQuestions[qIndex].correct_answer = newQuestions[qIndex].options[0];
                                            }
                                            setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                          }}
                                          className='text-red-600 hover:text-red-700'
                                        >
                                          <X className='h-4 w-4' />
                                        </Button>
                                      )}
                                    </div>
                                  ))}

                                  {/* Add Option Button */}
                                  {isEditingQuiz && (
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      onClick={() => {
                                        const newQuestions = [...editedQuizData.questions];
                                        newQuestions[qIndex].options.push('New option');
                                        setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                      }}
                                      className='w-full'
                                    >
                                      <Plus className='h-4 w-4 mr-1' />
                                      Add Option
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* True/False or Short Answer Questions */}
                                  <label className='text-xs font-medium text-gray-500'>
                                    {question.type === 'true_false' ? 'Answer' : 'Correct Answer'}
                                  </label>

                                  {isEditingQuiz ? (
                                    <div className='space-y-2'>
                                      {question.type === 'true_false' ? (
                                        <div className='flex gap-2'>
                                          <Button
                                            type='button'
                                            variant={question.correct_answer === true ? 'default' : 'outline'}
                                            size='sm'
                                            onClick={() => {
                                              const newQuestions = [...editedQuizData.questions];
                                              newQuestions[qIndex].correct_answer = true;
                                              setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                            }}
                                            className='flex-1'
                                          >
                                            <CheckCircle2 className='h-4 w-4 mr-1' />
                                            True
                                          </Button>
                                          <Button
                                            type='button'
                                            variant={question.correct_answer === false ? 'default' : 'outline'}
                                            size='sm'
                                            onClick={() => {
                                              const newQuestions = [...editedQuizData.questions];
                                              newQuestions[qIndex].correct_answer = false;
                                              setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                            }}
                                            className='flex-1'
                                          >
                                            <X className='h-4 w-4 mr-1' />
                                            False
                                          </Button>
                                        </div>
                                      ) : (
                                        <Textarea
                                          value={question.correct_answer || ''}
                                          onChange={(e) => {
                                            const newQuestions = [...editedQuizData.questions];
                                            newQuestions[qIndex].correct_answer = e.target.value;
                                            setEditedQuizData({ ...editedQuizData, questions: newQuestions });
                                          }}
                                          placeholder='Enter the correct answer'
                                          rows={2}
                                          className='bg-white'
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <div className='p-3 rounded border bg-green-50 border-green-300'>
                                      <div className='flex items-center gap-2 text-sm font-medium text-green-900'>
                                        <CheckCircle2 className='h-4 w-4 text-green-600' />
                                        {question.type === 'true_false'
                                          ? (question.correct_answer ? 'True' : 'False')
                                          : question.correct_answer
                                        }
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Add Question Button */}
                        {isEditingQuiz && (
                          <Button
                            type='button'
                            variant='outline'
                            onClick={() => {
                              const newQuestion = {
                                question: 'New question',
                                type: 'multiple_choice',
                                options: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
                                correct_answer: 'Option 1'
                              };
                              setEditedQuizData({
                                ...editedQuizData,
                                questions: [...editedQuizData.questions, newQuestion]
                              });
                            }}
                            className='w-full'
                          >
                            <Plus className='h-4 w-4 mr-1' />
                            Add Question
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className='flex justify-between pt-6 border-t'>
                  <Button
                    variant='outline'
                    onClick={() => setShowRegenerateDialog(true)}
                    disabled={generatingQuiz || submitting}
                  >
                    <Sparkles className='h-4 w-4 mr-2' />
                    Regenerate Quiz
                  </Button>

                  <Button
                    onClick={handleCreatePost}
                    disabled={!quizData || generatingQuiz || submitting}
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner />
                        Creating Post...
                      </>
                    ) : (
                      'Create Post with Quiz'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>

      {/* Regenerate Quiz Confirmation Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <Sparkles className="h-6 w-6 text-amber-600" />
            </div>
            <DialogTitle className="text-center">Regenerate Quiz?</DialogTitle>
            <DialogDescription className="text-center">
              This will generate a new quiz from your {savedData?.postType === 'text' ? 'content' : savedData?.postType || 'content'}. Your current quiz and any edits will be lost.
            </DialogDescription>
          </DialogHeader>

          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              The AI will create a completely new set of questions. This action cannot be undone.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowRegenerateDialog(false)}
              disabled={generatingQuiz}
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => {
                setShowRegenerateDialog(false);
                if (savedData.postType === 'document') {
                  generateQuiz(null, savedData.formData.title, savedData.formData.documentUrl, 'document');
                } else if (savedData.postType === 'text') {
                  generateQuiz(savedData.formData.content, savedData.formData.title, null, 'text');
                } else {
                  generateQuiz(savedData.formData.transcriptionRaw, savedData.formData.title, savedData.formData.videoUrl, 'video');
                }
              }}
              disabled={generatingQuiz}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Regenerate Quiz
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell3>
  );
}
